import { useRef, useEffect, useCallback, useState } from 'react';
import { useProjectStore } from '@/store/projectStore';
import {
  renderOverlays,
  getOverlayAtPoint,
  getResizeHandleAtPoint,
} from '@/utils/canvasRenderer';
import { createOverlay } from '@/utils/overlayFactory';
import {
  calculateSnapAndGuides,
  drawAlignmentGuides,
  type AlignmentGuide,
} from '@/utils/alignmentGuides';
import type { Point, ResizeHandle, Tool, Bounds, OverlayElement } from '@/types';

interface CanvasOverlayProps {
  videoWidth: number;
  videoHeight: number;
  containerWidth: number;
  containerHeight: number;
  videoRef?: React.RefObject<HTMLVideoElement>;
}

/** Smallest overlay side, in video pixels. */
const MIN_SIZE = 20;

/**
 * How much of an overlay must stay inside the frame, in video pixels.
 *
 * Overlays used to be hard-clamped so they could never cross the frame edge,
 * which made half-off-frame watermarks and lower-thirds impossible and made
 * dragging feel like it hit a wall. They now move freely in and out of frame;
 * this margin only guarantees there is always something left to grab, so an
 * overlay can't be flung out of reach.
 */
const KEEP_GRABBABLE = 24;

// ─── Gesture state (refs, not React state — a re-render per mousemove is jank) ─

interface GestureState {
  /** Pointer position, in video space, when the gesture began. */
  startPoint: Point;
  /** Overlay bounds when the gesture began. */
  startBounds: Bounds;
  /** Overlay rotation when the gesture began. */
  startRotation: number;
  /** Pointer angle (deg) relative to the overlay centre at gesture start. */
  startPointerAngle: number;
  overlayId: string;
}

interface PinchState {
  /** Distance between two fingers at gesture start (px on screen). */
  startDist: number;
  /** Angle (degrees) between two fingers at gesture start. */
  startAngle: number;
  /** Overlay bounds at gesture start (video-space px). */
  overlayBounds: Bounds;
  /** Overlay rotation at gesture start (degrees). */
  overlayRotation: number;
}

const getPinchInfo = (t1: Touch, t2: Touch): { dist: number; angle: number } => {
  const dx = t2.clientX - t1.clientX;
  const dy = t2.clientY - t1.clientY;
  return {
    dist: Math.sqrt(dx * dx + dy * dy),
    angle: Math.atan2(dy, dx) * (180 / Math.PI),
  };
};

/** Angle in degrees from an overlay's centre to a point. */
const angleFromCentre = (bounds: Bounds, point: Point): number => {
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  return (Math.atan2(point.y - cy, point.x - cx) * 180) / Math.PI;
};

/**
 * Resize `start` by a pointer delta expressed in the overlay's OWN (un-rotated)
 * frame, then map the result back to world space.
 *
 * Doing the arithmetic in the rotated frame is what makes a rotated overlay
 * resize along its own axes; the previous code applied the raw screen delta to
 * axis-aligned bounds, so dragging a handle on a rotated overlay moved it in a
 * direction unrelated to the handle you grabbed.
 */
const resizeBounds = (
  start: Bounds,
  rotation: number,
  handle: ResizeHandle,
  worldDx: number,
  worldDy: number,
  opts: { preserveAspect: boolean; fromCentre: boolean }
): Bounds => {
  // Rotate the world-space delta into the overlay's local frame.
  const rad = (-rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  let dx = worldDx * cos - worldDy * sin;
  let dy = worldDx * sin + worldDy * cos;

  const movesLeft = handle === 'nw' || handle === 'w' || handle === 'sw';
  const movesRight = handle === 'ne' || handle === 'e' || handle === 'se';
  const movesTop = handle === 'nw' || handle === 'n' || handle === 'ne';
  const movesBottom = handle === 'sw' || handle === 's' || handle === 'se';

  // Alt: grow/shrink symmetrically about the centre.
  if (opts.fromCentre) {
    dx *= 2;
    dy *= 2;
  }

  let width = start.width + (movesRight ? dx : 0) - (movesLeft ? dx : 0);
  let height = start.height + (movesBottom ? dy : 0) - (movesTop ? dy : 0);

  // Shift on a corner: keep the original proportions.
  if (opts.preserveAspect && (movesLeft || movesRight) && (movesTop || movesBottom)) {
    const aspect = start.width / Math.max(1, start.height);
    if (Math.abs(width - start.width) > Math.abs(height - start.height)) {
      height = width / aspect;
    } else {
      width = height * aspect;
    }
  }

  width = Math.max(MIN_SIZE, width);
  height = Math.max(MIN_SIZE, height);

  // Local top-left: an edge that isn't being dragged stays put.
  let localX = start.x;
  let localY = start.y;
  if (opts.fromCentre) {
    localX = start.x + start.width / 2 - width / 2;
    localY = start.y + start.height / 2 - height / 2;
  } else {
    if (movesLeft) localX = start.x + start.width - width;
    if (movesTop) localY = start.y + start.height - height;
  }

  if (!rotation) {
    return { x: localX, y: localY, width, height };
  }

  // Rotation happens about the overlay's centre, so a resize that moves the
  // centre in local space has to move it the same way in world space.
  const startCx = start.x + start.width / 2;
  const startCy = start.y + start.height / 2;
  const localCx = localX + width / 2;
  const localCy = localY + height / 2;
  const fwd = (rotation * Math.PI) / 180;
  const fCos = Math.cos(fwd);
  const fSin = Math.sin(fwd);
  const ddx = localCx - startCx;
  const ddy = localCy - startCy;
  const worldCx = startCx + (ddx * fCos - ddy * fSin);
  const worldCy = startCy + (ddx * fSin + ddy * fCos);

  return { x: worldCx - width / 2, y: worldCy - height / 2, width, height };
};

// ─────────────────────────────────────────────────────────────────────────────

export const CanvasOverlay = ({
  videoWidth,
  videoHeight,
  containerWidth,
  containerHeight,
  videoRef,
}: CanvasOverlayProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createStart, setCreateStart] = useState<Point | null>(null);
  const [createCurrent, setCreateCurrent] = useState<Point | null>(null);
  const [alignmentGuides, setAlignmentGuides] = useState<AlignmentGuide[]>([]);

  const gestureRef = useRef<GestureState | null>(null);
  const pinchRef = useRef<PinchState | null>(null);

  const overlays = useProjectStore((state) => state.project.overlays);
  const video = useProjectStore((state) => state.project.video);
  const currentTime = useProjectStore((state) => state.playerState.currentTime);
  const isPlaying = useProjectStore((state) => state.playerState.isPlaying);
  const selectedTool = useProjectStore((state) => state.selectedTool);
  const selectedIds = useProjectStore((state) => state.selectionState.selectedIds);
  const hoveredId = useProjectStore((state) => state.selectionState.hoveredId);
  const dragState = useProjectStore((state) => state.dragState);

  const addOverlay = useProjectStore((state) => state.addOverlay);
  const updateOverlay = useProjectStore((state) => state.updateOverlay);
  const selectOverlay = useProjectStore((state) => state.selectOverlay);
  const deselectAll = useProjectStore((state) => state.deselectAll);
  const setHoveredOverlay = useProjectStore((state) => state.setHoveredOverlay);
  const startDrag = useProjectStore((state) => state.startDrag);
  const endDrag = useProjectStore((state) => state.endDrag);
  const pushHistory = useProjectStore((state) => state.pushHistory);

  // Calculate scale to fit video in container
  const scale = Math.min(containerWidth / videoWidth, containerHeight / videoHeight);
  const offsetX = (containerWidth - videoWidth * scale) / 2;
  const offsetY = (containerHeight - videoHeight * scale) / 2;

  // Convert screen coordinates to video coordinates
  const screenToVideo = useCallback((screenX: number, screenY: number): Point => {
    return {
      x: (screenX - offsetX) / scale,
      y: (screenY - offsetY) / scale,
    };
  }, [offsetX, offsetY, scale]);

  // Clamp a point to stay within video bounds
  const clampToVideo = useCallback((point: Point): Point => {
    return {
      x: Math.max(0, Math.min(videoWidth, point.x)),
      y: Math.max(0, Math.min(videoHeight, point.y)),
    };
  }, [videoWidth, videoHeight]);

  /**
   * Keep an overlay reachable without confining it to the frame.
   *
   * Overlays may hang off any edge — that is how watermarks, lower-thirds and
   * bleed-off graphics are placed — as long as a sliver stays on screen.
   */
  const constrainBounds = useCallback((bounds: Bounds): Bounds => {
    const keepX = Math.min(KEEP_GRABBABLE, bounds.width);
    const keepY = Math.min(KEEP_GRABBABLE, bounds.height);
    return {
      width: Math.max(MIN_SIZE, bounds.width),
      height: Math.max(MIN_SIZE, bounds.height),
      x: Math.max(keepX - bounds.width, Math.min(videoWidth - keepX, bounds.x)),
      y: Math.max(keepY - bounds.height, Math.min(videoHeight - keepY, bounds.y)),
    };
  }, [videoWidth, videoHeight]);

  // ── Rendering ──────────────────────────────────────────────────────────────

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Only resize when it actually changed — assigning width/height clears the
    // canvas and reallocates its backing store on every frame otherwise.
    if (canvas.width !== containerWidth || canvas.height !== containerHeight) {
      canvas.width = containerWidth;
      canvas.height = containerHeight;
    }

    ctx.clearRect(0, 0, containerWidth, containerHeight);
    ctx.save();
    ctx.translate(offsetX, offsetY);

    // Render all overlays (pass video element for blur effects). The last
    // argument re-runs this pass once an image overlay's bitmap has decoded —
    // without it a logo dropped on a paused video stayed invisible until
    // something else happened to trigger a repaint.
    renderOverlays(
      ctx,
      overlays,
      currentTime,
      scale,
      selectedIds,
      hoveredId,
      videoRef?.current,
      () => requestAnimationFrame(() => renderCanvasRef.current?.())
    );

    // Draw alignment guides when dragging
    if (alignmentGuides.length > 0) {
      drawAlignmentGuides(ctx, alignmentGuides, videoWidth * scale, videoHeight * scale);
    }

    // Draw creation preview
    if (isCreating && createStart && createCurrent) {
      ctx.strokeStyle = '#6366f1';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);

      const x = Math.min(createStart.x, createCurrent.x) * scale;
      const y = Math.min(createStart.y, createCurrent.y) * scale;
      const width = Math.abs(createCurrent.x - createStart.x) * scale;
      const height = Math.abs(createCurrent.y - createStart.y) * scale;

      ctx.strokeRect(x, y, width, height);
    }

    ctx.restore();
  }, [
    overlays,
    currentTime,
    scale,
    offsetX,
    offsetY,
    containerWidth,
    containerHeight,
    selectedIds,
    hoveredId,
    isCreating,
    createStart,
    createCurrent,
    videoRef,
    alignmentGuides,
    videoWidth,
    videoHeight,
  ]);

  // Stable handle so async callbacks always reach the current renderer.
  const renderCanvasRef = useRef(renderCanvas);
  renderCanvasRef.current = renderCanvas;

  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  /**
   * Repaint every animation frame while the video plays.
   *
   * The canvas used to repaint only when `currentTime` changed in the store,
   * and that comes from the media element's `timeupdate` event — roughly four
   * times a second. Anything sampling the video (blur/pixelate regions) or
   * driven by time (overlay animations) therefore updated in visible steps and
   * lagged the picture underneath it by up to a quarter of a second.
   */
  useEffect(() => {
    if (!isPlaying) return;
    let frame = 0;
    const tick = () => {
      renderCanvasRef.current?.();
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isPlaying]);

  // Get cursor style based on tool and hover state
  const getCursor = useCallback((): string => {
    // During drag operations, show appropriate cursor
    if (dragState.isDragging) {
      if (dragState.dragType === 'rotate') return 'grabbing';
      if (dragState.dragType === 'move') return 'move';
      if (dragState.resizeHandle) {
        const handleCursors: Record<ResizeHandle, string> = {
          nw: 'nw-resize',
          n: 'n-resize',
          ne: 'ne-resize',
          w: 'w-resize',
          e: 'e-resize',
          sw: 'sw-resize',
          s: 's-resize',
          se: 'se-resize',
          rotate: 'grabbing',
        };
        return handleCursors[dragState.resizeHandle];
      }
    }

    // If hovering over an existing overlay, show move cursor (regardless of tool)
    if (hoveredId) {
      return 'move';
    }

    // Default cursor based on tool
    if (selectedTool === 'select') {
      return 'default';
    }

    // Drawing tools show crosshair when not over an overlay
    return 'crosshair';
  }, [selectedTool, dragState, hoveredId]);

  // ── Shared gesture application ─────────────────────────────────────────────

  /** Begin a move/resize/rotate gesture on `overlay`. */
  const beginGesture = useCallback((
    overlay: OverlayElement,
    point: Point,
    handle: ResizeHandle | null
  ) => {
    gestureRef.current = {
      startPoint: point,
      startBounds: { ...overlay.bounds },
      startRotation: overlay.rotation,
      startPointerAngle: angleFromCentre(overlay.bounds, point),
      overlayId: overlay.id,
    };
    startDrag({
      dragType: handle ? (handle === 'rotate' ? 'rotate' : 'resize') : 'move',
      startPoint: point,
      currentPoint: point,
      resizeHandle: handle ?? undefined,
    });
  }, [startDrag]);

  /**
   * Apply the in-flight gesture for the current pointer position.
   *
   * Everything is derived from the gesture's ORIGIN rather than from the
   * previous pointer sample. Accumulating frame-to-frame deltas (what this did
   * before) drifts whenever a sample is dropped, and it needed a store write
   * per mousemove just to remember the last point.
   */
  const applyGesture = useCallback((
    point: Point,
    modifiers: { shift: boolean; alt: boolean; meta: boolean }
  ) => {
    const gesture = gestureRef.current;
    if (!gesture) return;

    const overlay = overlays.find((o) => o.id === gesture.overlayId);
    if (!overlay || overlay.locked) return;

    const dx = point.x - gesture.startPoint.x;
    const dy = point.y - gesture.startPoint.y;

    if (dragState.dragType === 'move') {
      const moved: Bounds = {
        ...gesture.startBounds,
        x: gesture.startBounds.x + dx,
        y: gesture.startBounds.y + dy,
      };

      // Alt / Cmd suspends snapping so an overlay can be placed anywhere,
      // including a hair off a guide. There was previously no way to opt out.
      if (modifiers.alt || modifiers.meta) {
        updateOverlay(overlay.id, { bounds: constrainBounds(moved) });
        setAlignmentGuides([]);
        return;
      }

      const otherOverlays = overlays.filter((o) => o.id !== overlay.id && o.visible);
      const snapResult = calculateSnapAndGuides(
        moved,
        videoWidth,
        videoHeight,
        otherOverlays,
        scale
      );

      updateOverlay(overlay.id, {
        bounds: constrainBounds({ ...moved, x: snapResult.x, y: snapResult.y }),
      });
      setAlignmentGuides(snapResult.guides);
      return;
    }

    if (dragState.dragType === 'resize' && dragState.resizeHandle && dragState.resizeHandle !== 'rotate') {
      const resized = resizeBounds(
        gesture.startBounds,
        gesture.startRotation,
        dragState.resizeHandle,
        dx,
        dy,
        { preserveAspect: modifiers.shift, fromCentre: modifiers.alt }
      );
      updateOverlay(overlay.id, { bounds: constrainBounds(resized) });
      return;
    }

    if (dragState.dragType === 'rotate') {
      // Rotate relative to where the handle was grabbed, so the overlay does
      // not snap to the pointer the instant the handle is picked up.
      const delta = angleFromCentre(gesture.startBounds, point) - gesture.startPointerAngle;
      let degrees = (gesture.startRotation + delta) % 360;
      if (degrees < 0) degrees += 360;
      // Shift snaps to 15° increments.
      if (modifiers.shift) degrees = Math.round(degrees / 15) * 15;
      updateOverlay(overlay.id, { rotation: degrees });
    }
  }, [overlays, dragState, updateOverlay, constrainBounds, videoWidth, videoHeight, scale]);

  const finishGesture = useCallback(() => {
    if (dragState.isDragging) {
      if (
        dragState.dragType === 'move' ||
        dragState.dragType === 'resize' ||
        dragState.dragType === 'rotate'
      ) {
        pushHistory(`${dragState.dragType} overlay`);
      }
      endDrag();
    }
    gestureRef.current = null;
    setAlignmentGuides([]);
  }, [dragState, endDrag, pushHistory]);

  // ── Mouse Handlers ─────────────────────────────────────────────────────────

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !video) return;

    const videoPoint = screenToVideo(e.clientX - rect.left, e.clientY - rect.top);

    // First, check if clicking on an existing overlay (works regardless of tool)
    const clickedOverlay = getOverlayAtPoint(overlays, videoPoint.x, videoPoint.y, currentTime);

    if (clickedOverlay) {
      // A locked overlay can be selected but not moved. The touch path already
      // honoured `locked`; the mouse path ignored it entirely.
      if (clickedOverlay.locked) {
        if (!selectedIds.includes(clickedOverlay.id)) {
          selectOverlay(clickedOverlay.id, e.shiftKey);
        }
        return;
      }

      // Check if clicking on a selected overlay's resize/rotate handle
      if (selectedIds.includes(clickedOverlay.id) && selectedIds.length === 1) {
        const handle = getResizeHandleAtPoint(clickedOverlay, videoPoint.x, videoPoint.y, scale);
        if (handle) {
          beginGesture(clickedOverlay, videoPoint, handle);
          return;
        }
      }

      // Select the overlay
      if (e.shiftKey) {
        selectOverlay(clickedOverlay.id, true);
      } else if (!selectedIds.includes(clickedOverlay.id)) {
        selectOverlay(clickedOverlay.id);
      }

      beginGesture(clickedOverlay, videoPoint, null);
      return;
    }

    // Clicked on empty space
    if (selectedTool === 'select') {
      deselectAll();
    } else {
      const clampedPoint = clampToVideo(videoPoint);
      setIsCreating(true);
      setCreateStart(clampedPoint);
      setCreateCurrent(clampedPoint);
    }
  }, [
    video,
    selectedTool,
    overlays,
    currentTime,
    selectedIds,
    scale,
    screenToVideo,
    selectOverlay,
    deselectAll,
    beginGesture,
    clampToVideo,
  ]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !video) return;

    // In-flight drags and in-flight creation are both driven by the window
    // listeners further down, so they keep tracking outside the canvas.
    if (isCreating || dragState.isDragging) return;

    const videoPoint = screenToVideo(e.clientX - rect.left, e.clientY - rect.top);
    const hoveredOverlay = getOverlayAtPoint(overlays, videoPoint.x, videoPoint.y, currentTime);
    setHoveredOverlay(hoveredOverlay?.id ?? null);
  }, [
    video,
    isCreating,
    dragState.isDragging,
    overlays,
    currentTime,
    screenToVideo,
    setHoveredOverlay,
  ]);

  const commitCreation = useCallback(() => {
    if (!video || !isCreating || !createStart || !createCurrent) return;

    const bounds: Bounds = {
      x: Math.min(createStart.x, createCurrent.x),
      y: Math.min(createStart.y, createCurrent.y),
      width: Math.abs(createCurrent.x - createStart.x),
      height: Math.abs(createCurrent.y - createStart.y),
    };

    if (bounds.width > 10 && bounds.height > 10) {
      const toolToType: Record<Tool, string> = {
        select: '',
        rectangle: 'rectangle',
        'rounded-rectangle': 'rounded-rectangle',
        circle: 'circle',
        ellipse: 'ellipse',
        polygon: 'polygon',
        text: 'text',
        image: 'image',
        blur: 'blur',
      };

      const overlayType = toolToType[selectedTool];
      if (overlayType) {
        const newOverlay = createOverlay(overlayType as any, {
          bounds: constrainBounds(bounds),
          startTime: 0,
          endTime: video.duration,
          zIndex: overlays.length,
        });
        addOverlay(newOverlay);
      }
    }

    setIsCreating(false);
    setCreateStart(null);
    setCreateCurrent(null);
  }, [
    video,
    isCreating,
    createStart,
    createCurrent,
    selectedTool,
    overlays,
    addOverlay,
    constrainBounds,
  ]);

  const handleMouseLeave = useCallback(() => {
    // Only clear hover. The drag itself continues on the window listeners —
    // previously letting the pointer leave the canvas mid-drag left the drag
    // running with no way to end it, so the overlay stuck to the cursor.
    if (!dragState.isDragging) {
      setHoveredOverlay(null);
    }
  }, [dragState.isDragging, setHoveredOverlay]);

  // Latest handlers, so the window listeners below can stay bound for a whole
  // gesture instead of being torn down and re-added on every mouse move (they
  // close over `overlays`, which changes with each update).
  const applyGestureRef = useRef(applyGesture);
  applyGestureRef.current = applyGesture;
  const finishGestureRef = useRef(finishGesture);
  finishGestureRef.current = finishGesture;
  const screenToVideoRef = useRef(screenToVideo);
  screenToVideoRef.current = screenToVideo;
  const commitCreationRef = useRef(commitCreation);
  commitCreationRef.current = commitCreation;
  const clampToVideoRef = useRef(clampToVideo);
  clampToVideoRef.current = clampToVideo;

  const pointFromEvent = useCallback((e: MouseEvent): Point | null => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return screenToVideoRef.current(e.clientX - rect.left, e.clientY - rect.top);
  }, []);

  /**
   * Window-level listeners for the duration of a drag.
   *
   * Without these the gesture only tracked while the pointer stayed over the
   * canvas: dragging out to the timeline or the inspector froze the overlay
   * mid-move, and releasing the button outside never ended the drag — the
   * overlay then stayed glued to the cursor.
   */
  useEffect(() => {
    if (!dragState.isDragging) return;

    const onMove = (e: MouseEvent) => {
      const point = pointFromEvent(e);
      if (!point) return;
      applyGestureRef.current(point, { shift: e.shiftKey, alt: e.altKey, meta: e.metaKey });
    };
    const onUp = () => finishGestureRef.current();

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragState.isDragging, pointFromEvent]);

  /** Same treatment for drawing a new overlay: finish it wherever the release lands. */
  useEffect(() => {
    if (!isCreating) return;

    const onMove = (e: MouseEvent) => {
      const point = pointFromEvent(e);
      if (!point) return;
      setCreateCurrent(clampToVideoRef.current(point));
    };
    const onUp = () => commitCreationRef.current();

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isCreating, pointFromEvent]);

  // ── Touch Handlers ─────────────────────────────────────────────────────────
  // Single finger  → tap to select + drag to move
  // Two fingers    → pinch-to-scale + two-finger rotate (on a selected overlay)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !video) return;

    if (e.touches.length === 1) {
      pinchRef.current = null;
      const t = e.touches[0];
      const vp = screenToVideo(t.clientX - rect.left, t.clientY - rect.top);

      const clickedOverlay = getOverlayAtPoint(overlays, vp.x, vp.y, currentTime);

      if (clickedOverlay) {
        if (!selectedIds.includes(clickedOverlay.id)) {
          selectOverlay(clickedOverlay.id);
        }
        if (clickedOverlay.locked) return;

        if (selectedIds.includes(clickedOverlay.id) && selectedIds.length === 1) {
          const handle = getResizeHandleAtPoint(clickedOverlay, vp.x, vp.y, scale);
          if (handle) {
            beginGesture(clickedOverlay, vp, handle);
            return;
          }
        }
        beginGesture(clickedOverlay, vp, null);
      } else {
        deselectAll();
      }
    } else if (e.touches.length === 2) {
      // Cancel any active single-finger drag first
      if (dragState.isDragging) {
        endDrag();
        gestureRef.current = null;
        setAlignmentGuides([]);
      }
      if (isCreating) {
        setIsCreating(false);
        setCreateStart(null);
        setCreateCurrent(null);
      }

      if (selectedIds.length === 1) {
        const overlay = overlays.find((o) => o.id === selectedIds[0]);
        if (overlay && !overlay.locked) {
          const { dist, angle } = getPinchInfo(e.touches[0], e.touches[1]);
          pinchRef.current = {
            startDist: dist,
            startAngle: angle,
            overlayBounds: { ...overlay.bounds },
            overlayRotation: overlay.rotation,
          };
        }
      }
    }
  }, [
    video, overlays, currentTime, selectedIds, scale, dragState.isDragging, isCreating,
    screenToVideo, selectOverlay, deselectAll, beginGesture, endDrag,
  ]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !video) return;

    if (e.touches.length === 1 && dragState.isDragging) {
      const t = e.touches[0];
      const vp = screenToVideo(t.clientX - rect.left, t.clientY - rect.top);
      // Touch has no modifier keys — plain move/resize/rotate.
      applyGesture(vp, { shift: false, alt: false, meta: false });
    } else if (e.touches.length === 2 && pinchRef.current) {
      const pinch = pinchRef.current;
      const { dist: newDist, angle: newAngle } = getPinchInfo(e.touches[0], e.touches[1]);

      if (selectedIds.length === 1) {
        const overlay = overlays.find((o) => o.id === selectedIds[0]);
        if (overlay && !overlay.locked) {
          const scaleFactor = Math.max(0.05, newDist / pinch.startDist);
          const angleDelta = newAngle - pinch.startAngle;

          const newWidth = Math.max(MIN_SIZE, pinch.overlayBounds.width * scaleFactor);
          const newHeight = Math.max(MIN_SIZE, pinch.overlayBounds.height * scaleFactor);
          const cx = pinch.overlayBounds.x + pinch.overlayBounds.width / 2;
          const cy = pinch.overlayBounds.y + pinch.overlayBounds.height / 2;

          const newBounds = constrainBounds({
            x: cx - newWidth / 2,
            y: cy - newHeight / 2,
            width: newWidth,
            height: newHeight,
          });
          const newRotation = ((pinch.overlayRotation + angleDelta) + 360) % 360;

          updateOverlay(overlay.id, { bounds: newBounds, rotation: newRotation });
        }
      }
    }
  }, [
    video, dragState.isDragging, overlays, selectedIds,
    screenToVideo, constrainBounds, applyGesture, updateOverlay,
  ]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    e.preventDefault();

    if (e.touches.length === 0) {
      finishGesture();
      if (pinchRef.current) {
        pushHistory('touch scale/rotate overlay');
        pinchRef.current = null;
      }
    } else if (e.touches.length === 1 && pinchRef.current) {
      pinchRef.current = null;
    }
  }, [finishGesture, pushHistory]);

  // Register touch handlers with { passive: false } so we can call preventDefault()
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const opts: AddEventListenerOptions = { passive: false };
    canvas.addEventListener('touchstart', handleTouchStart, opts);
    canvas.addEventListener('touchmove', handleTouchMove, opts);
    canvas.addEventListener('touchend', handleTouchEnd, opts);
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 no-select"
      style={{ cursor: getCursor(), touchAction: 'none' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    />
  );
};
